import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Card,
  Upload,
  Button,
  Table,
  message,
  Modal,
  Form,
  Select,
  Space,
  Tag,
  Typography,
  Tabs,
  DatePicker,
  Input,
  Row,
  Col,
  Tooltip,
  Alert,
  Segmented,
  Divider,
  Progress,
  List,
} from 'antd';
import {
  DeleteOutlined,
  CheckOutlined,
  SearchOutlined,
  ImportOutlined,
  InboxOutlined,
  FileTextOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { LoginContext } from '../../../context/loginContext';

const { Text, Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const ACCENT = '#FB6B48';
const PAGE_BG = '#f7f8fa';

const ImportarMovimientoAdministrador = () => {
  const { user, selectedEmpresa } = useContext(LoginContext);
  const [tipoCsv, setTipoCsv] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [totalSinFiltro, setTotalSinFiltro] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [tiposComp, setTiposComp] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowImputar, setRowImputar] = useState(null);
  const [imputando, setImputando] = useState(false);
  const [form] = Form.useForm();
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterComprobante, setFilterComprobante] = useState('');
  const [filterFechas, setFilterFechas] = useState(null);
  const [filterVinculoCuenta, setFilterVinculoCuenta] = useState('all');
  const [debouncedComprobante, setDebouncedComprobante] = useState('');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadStepText, setUploadStepText] = useState('');

  const authHeaders = { Authorization: `Bearer ${user?.token}` };
  const api = process.env.REACT_APP_API_URL;

  const loadMaestros = useCallback(async () => {
    if (!selectedEmpresa?.emp_id || !user?.token) return;
    const emp = selectedEmpresa.emp_id;
    const cfg = { headers: authHeaders };
    try {
      let cuentasRes = { data: { cuentas: [] } };
      try {
        cuentasRes = await axios.get(`${api}/cuenta/empresa/${emp}?page=1&limit=2000`, cfg);
      } catch (e) {
        if (e.response?.status !== 404) throw e;
      }
      const [pl, ce, tc] = await Promise.all([
        axios.get(`${api}/plan-cuenta/${emp}`, cfg),
        axios.get(`${api}/centro-costo/${emp}`, cfg),
        axios.get(`${api}/tipos-comprobantes`, cfg),
      ]);
      setCuentas(cuentasRes.data.cuentas || []);
      setPlanes(pl.data || []);
      setCentros(ce.data || []);
      setTiposComp(tc.data || []);
    } catch (e) {
      console.error(e);
      message.error('Error al cargar cuentas, plan o centros');
    }
  }, [api, selectedEmpresa?.emp_id, user?.token]);

  const loadPendientes = useCallback(async () => {
    if (!selectedEmpresa?.emp_id || !user?.token) return;
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      if (filterTipo !== 'all') params.set('tipo', filterTipo);
      if (debouncedComprobante.trim()) {
        params.set('comprobante', debouncedComprobante.trim());
      }
      if (filterFechas?.[0] && dayjs.isDayjs(filterFechas[0])) {
        params.set('fecha_desde', filterFechas[0].format('YYYY-MM-DD'));
      }
      if (filterFechas?.[1] && dayjs.isDayjs(filterFechas[1])) {
        params.set('fecha_hasta', filterFechas[1].format('YYYY-MM-DD'));
      }
      if (filterVinculoCuenta === 'sin' || filterVinculoCuenta === 'con') {
        params.set('vinculo_cuenta', filterVinculoCuenta);
      }
      const qs = params.toString();
      const { data } = await axios.get(
        `${api}/arca-import/pendientes/${selectedEmpresa.emp_id}${qs ? `?${qs}` : ''}`,
        { headers: authHeaders }
      );
      setPendientes(Array.isArray(data.items) ? data.items : []);
      setTotalSinFiltro(
        typeof data.total_sin_filtro === 'number' ? data.total_sin_filtro : 0
      );
    } catch (e) {
      console.error(e);
      message.error('No se pudieron cargar los pendientes');
      setPendientes([]);
      setTotalSinFiltro(0);
    } finally {
      setLoadingList(false);
    }
  }, [
    api,
    selectedEmpresa?.emp_id,
    user?.token,
    filterTipo,
    debouncedComprobante,
    filterFechas,
    filterVinculoCuenta,
  ]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedComprobante(filterComprobante), 400);
    return () => clearTimeout(t);
  }, [filterComprobante]);

  useEffect(() => {
    loadMaestros();
  }, [loadMaestros]);

  useEffect(() => {
    loadPendientes();
  }, [loadPendientes]);

  const parsePayload = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return raw;
  };

  const hayFiltros =
    filterTipo !== 'all' ||
    !!filterComprobante.trim() ||
    !!(filterFechas?.[0] && dayjs.isDayjs(filterFechas[0])) ||
    !!(filterFechas?.[1] && dayjs.isDayjs(filterFechas[1])) ||
    filterVinculoCuenta !== 'all';

  const eliminarTodosPendientes = () => {
    if (!selectedEmpresa?.emp_id) return;
    Modal.confirm({
      title: '¿Eliminar todos los pendientes?',
      content: (
        <span>
          Se eliminarán <strong>todos</strong> los comprobantes ARCA pendientes de imputación de esta
          empresa ({totalSinFiltro} registro{totalSinFiltro !== 1 ? 's' : ''}), sin importar los filtros
          de la tabla. Los movimientos ya generados en la importación también se eliminarán. Esta
          acción no se puede deshacer.
        </span>
      ),
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const { data } = await axios.delete(
            `${api}/arca-import/pendientes/${selectedEmpresa.emp_id}/todos`,
            { headers: authHeaders }
          );
          message.success(
            `Se eliminaron ${data.eliminados ?? 0} pendiente(s)`
          );
          await loadPendientes();
        } catch (e) {
          message.error(e.response?.data?.message || 'No se pudo eliminar');
          return Promise.reject(e);
        }
      },
    });
  };

  const handleUpload = async (file) => {
    if (!selectedEmpresa?.emp_id) {
      message.warning('Seleccione una empresa');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('empresaId', String(selectedEmpresa.emp_id));
    fd.append('tipo', String(tipoCsv));
    setUploadPercent(0);
    setUploadStepText('Subiendo archivo…');
    setUploading(true);
    try {
      const { data } = await axios.post(`${api}/arca-import/upload`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          const total = evt.total || file.size || 0;
          if (!total) return;
          const raw = Math.round((evt.loaded * 100) / total);
          const pct = Math.min(raw, 95);
          setUploadPercent(pct);
          if (raw >= 100) {
            setUploadStepText('Archivo subido. Procesando comprobantes…');
          }
        },
      });
      setUploadPercent(100);
      setUploadStepText('Importación finalizada');
      if (data.inserted > 0) {
        message.success(`Importadas ${data.inserted} fila(s) nueva(s)`);
      }
      if (data.skipped_duplicates > 0) {
        message.info(
          `${data.skipped_duplicates} fila(s) omitidas: mismo comprobante ya en pendientes o ya cargado como movimiento.`
        );
      }
      if (
        data.inserted === 0 &&
        !data.skipped_duplicates &&
        !(data.warnings && data.warnings.length)
      ) {
        message.success('Importadas 0 filas nuevas');
      }
      if (data.warnings?.length) {
        data.warnings.forEach((w) =>
          message.warning(`Fila ${w.row}: ${w.message}`, 5)
        );
      }
      const cuentasAgregadas = Number(data.cuentas_agregadas || 0);
      const observaciones = Array.isArray(data.observaciones) ? data.observaciones : [];
      const problemas = Array.isArray(data.warnings) ? data.warnings : [];
      Modal.info({
        title: 'Resultado de la importación',
        width: 780,
        content: (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>
              Importadas: <Text strong>{data.inserted || 0}</Text> · Duplicadas omitidas:{' '}
              <Text strong>{data.skipped_duplicates || 0}</Text> · Cuentas agregadas:{' '}
              <Text strong>{cuentasAgregadas}</Text>
            </Text>
            {observaciones.length > 0 ? (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Observaciones
                </Text>
                <List
                  size="small"
                  bordered
                  dataSource={observaciones.slice(0, 12)}
                  locale={{ emptyText: 'Sin observaciones' }}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
                {observaciones.length > 12 ? (
                  <Text type="secondary">… y {observaciones.length - 12} observación(es) más.</Text>
                ) : null}
              </div>
            ) : null}
            {problemas.length > 0 ? (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Problemas detectados
                </Text>
                <List
                  size="small"
                  bordered
                  dataSource={problemas.slice(0, 12)}
                  renderItem={(w) => <List.Item>{`Fila ${w.row}: ${w.message}`}</List.Item>}
                />
                {problemas.length > 12 ? (
                  <Text type="secondary">… y {problemas.length - 12} problema(s) más.</Text>
                ) : null}
              </div>
            ) : (
              <Alert type="success" showIcon message="No se detectaron problemas en los comprobantes procesados." />
            )}
          </Space>
        ),
      });
      await loadPendientes();
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || 'Error al subir el CSV');
    } finally {
      setUploading(false);
      setUploadPercent(0);
      setUploadStepText('');
    }
    return false;
  };

  const eliminar = async (aipId) => {
    try {
      await axios.delete(
        `${api}/arca-import/pendiente/${aipId}?empresaId=${selectedEmpresa.emp_id}`,
        { headers: authHeaders }
      );
      message.success('Eliminado');
      loadPendientes();
    } catch (e) {
      message.error(e.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const abrirImputar = (record) => {
    setRowImputar(record);
    const p = parsePayload(record.aip_payload);
    const tieneMov = record.aip_movimiento != null;
    form.setFieldsValue({
      ...(tieneMov ? {} : { mov_cuenta: p.cue_sugerida_id || undefined }),
      des_plancuenta: undefined,
      des_centro: undefined,
      mov_tipocomprobante: p.tic_id || undefined,
    });
    setModalOpen(true);
  };

  const confirmarImputar = async () => {
    const p = parsePayload(rowImputar.aip_payload);
    const vals = await form.validateFields();
    if (!p.tic_id && !vals.mov_tipocomprobante) {
      message.error('Elija el tipo de comprobante');
      return;
    }
    const tieneMov = rowImputar.aip_movimiento != null;
    setImputando(true);
    try {
      const body = {
        empresaId: selectedEmpresa.emp_id,
        des_plancuenta: vals.des_plancuenta,
        des_centro: vals.des_centro,
        mov_tipocomprobante: vals.mov_tipocomprobante || undefined,
      };
      if (!tieneMov) {
        body.mov_cuenta = vals.mov_cuenta;
      }
      await axios.post(
        `${api}/arca-import/pendiente/${rowImputar.aip_id}/imputar`,
        body,
        { headers: authHeaders }
      );
      message.success(tieneMov ? 'Imputación registrada en el movimiento' : 'Movimiento creado');
      setModalOpen(false);
      setRowImputar(null);
      loadPendientes();
    } catch (e) {
      message.error(e.response?.data?.message || e.message || 'Error al imputar');
    } finally {
      setImputando(false);
    }
  };

  const pModal = rowImputar ? parsePayload(rowImputar.aip_payload) : {};

  const cellNowrap = { whiteSpace: 'nowrap' };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'aip_payload',
      key: 'fecha',
      width: '9%',
      ellipsis: true,
      onCell: () => ({ style: cellNowrap }),
      render: (payload) => parsePayload(payload).fecha,
    },
    {
      title: 'Comprobante',
      dataIndex: 'aip_payload',
      key: 'cbte',
      width: '11%',
      ellipsis: true,
      onCell: () => ({ style: cellNowrap }),
      render: (payload) => {
        const p = parsePayload(payload);
        return `${p.pto_vta}-${p.nro_comp}`;
      },
    },
    {
      title: 'Tipo',
      key: 'libro',
      width: '9%',
      align: 'center',
      onCell: () => ({ style: cellNowrap }),
      render: (_, r) =>
        r.aip_tipo === 1 ? <Tag color="blue">Compras</Tag> : <Tag color="green">Ventas</Tag>,
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Cód. AFIP</span>,
      dataIndex: 'aip_payload',
      key: 'afip',
      width: '7%',
      align: 'center',
      onCell: () => ({ style: cellNowrap }),
      render: (payload) => parsePayload(payload).tic_codigo_afip,
    },
    {
      title: 'Contraparte',
      dataIndex: 'aip_payload',
      key: 'razon',
      ellipsis: { showTitle: false },
      onCell: () => ({ style: { minWidth: 0 } }),
      render: (payload) => {
        const p = parsePayload(payload);
        const full = [p.cuit_contraparte, p.razon_contraparte].filter(Boolean).join(' ');
        const node = (
          <span>
            <Text type="secondary">{p.cuit_contraparte}</Text>
            {p.cuit_contraparte && p.razon_contraparte ? ' ' : null}
            {p.razon_contraparte}
          </span>
        );
        return <Tooltip title={full}>{node}</Tooltip>;
      },
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Cuenta CUIT</span>,
      dataIndex: 'aip_payload',
      key: 'vinculo_cuenta',
      width: '11%',
      align: 'center',
      onCell: () => ({ style: cellNowrap }),
      render: (payload) => {
        const p = parsePayload(payload);
        const tiene = p.cue_sugerida_id != null && String(p.cue_sugerida_id).trim() !== '';
        return tiene ? (
          <Tooltip title={p.cue_sugerida_nombre || `cue_id ${p.cue_sugerida_id}`}>
            <Space size={4} wrap>
              <Tag color="success">En sistema</Tag>
              {p.cuenta_creada_import ? <Tag color="processing">Alta en import</Tag> : null}
            </Space>
          </Tooltip>
        ) : (
          <Tooltip title="Registro anterior sin vínculo: elija la cuenta al imputar o dé de alta en Cuentas.">
            <Tag color="warning">Sin cuenta</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'aip_payload',
      key: 'total',
      width: '11%',
      align: 'right',
      onCell: () => ({ style: { ...cellNowrap, fontVariantNumeric: 'tabular-nums' } }),
      render: (payload) =>
        Number(parsePayload(payload).mov_total || 0).toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: 'Acciones',
      key: 'acc',
      width: '15%',
      align: 'center',
      onCell: () => ({ style: cellNowrap }),
      render: (_, r) => (
        <Space size="small" wrap={false}>
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => abrirImputar(r)}>
            Imputar
          </Button>
          <Button danger size="small" icon={<DeleteOutlined />} onClick={() => eliminar(r.aip_id)} />
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        width: 'calc(100% + 40px)',
        marginLeft: -20,
        marginRight: -20,
        marginTop: -8,
        boxSizing: 'border-box',
        background: PAGE_BG,
        minHeight: 'calc(100vh - 100px)',
        paddingBottom: 32,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 1280,
          margin: '0 auto',
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
          border: 'none',
        }}
        styles={{ body: { padding: 0 } }}
        title={
          <Space align="center" size={12}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${ACCENT}22, ${ACCENT}44)`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ImportOutlined style={{ fontSize: 22, color: ACCENT }} />
            </span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
                Importar movimientos (ARCA)
              </div>
              {selectedEmpresa?.emp_nombre ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Empresa: <Text strong>{selectedEmpresa.emp_nombre}</Text>
                </Text>
              ) : null}
            </div>
          </Space>
        }
      >
        <Tabs
          defaultActiveKey="subir"
          tabBarStyle={{
            margin: 0,
            padding: '0 24px',
            background: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
          }}
          items={[
            {
              key: 'subir',
              label: (
                <Space size={6}>
                  <FileTextOutlined />
                  <span>Subir CSV</span>
                </Space>
              ),
              children: (
                <div style={{ padding: '28px 24px 40px' }}>
                  <div style={{ maxWidth: 640, margin: '0 auto' }}>
                    <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                      Tipo de archivo
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                      Elegí si el CSV corresponde a comprobantes que recibiste (compras) o a los que
                      emitiste (ventas).
                    </Paragraph>
                    <Segmented
                      block
                      size="large"
                      value={tipoCsv === 1 ? 'compras' : 'ventas'}
                      onChange={(v) => setTipoCsv(v === 'compras' ? 1 : 2)}
                      options={[
                        { label: 'Comprobantes recibidos (compras)', value: 'compras' },
                        { label: 'Comprobantes emitidos (ventas)', value: 'ventas' },
                      ]}
                      style={{ marginBottom: 28 }}
                    />

                    <Title level={5} style={{ marginBottom: 8 }}>
                      Archivo CSV
                    </Title>
                    <Upload.Dragger
                      beforeUpload={handleUpload}
                      showUploadList={false}
                      accept=".csv,text/csv"
                      disabled={uploading}
                      style={{
                        borderRadius: 12,
                        borderStyle: 'dashed',
                        borderColor: '#d9d9d9',
                        background: '#fafafa',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                    >
                      <p className="ant-upload-drag-icon" style={{ marginBottom: 12 }}>
                        <InboxOutlined style={{ fontSize: 52, color: ACCENT }} />
                      </p>
                      <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500 }}>
                        {uploading ? 'Procesando…' : 'Arrastrá el CSV aquí o hacé clic para elegirlo'}
                      </p>
                      <p className="ant-upload-hint" style={{ maxWidth: 420, margin: '8px auto 0' }}>
                        Usá el archivo tal cual lo exporta ARCA. Separador: coma o punto y coma.
                        Tamaño máx. 20&nbsp;MB.
                      </p>
                    </Upload.Dragger>
                    {uploading ? (
                      <div style={{ marginTop: 12 }}>
                        <Progress
                          percent={uploadPercent}
                          status={uploadPercent < 100 ? 'active' : 'success'}
                          strokeColor={ACCENT}
                        />
                        <Text type="secondary">{uploadStepText || 'Procesando importación…'}</Text>
                        <Alert
                          style={{ marginTop: 10 }}
                          type="warning"
                          showIcon
                          message="Aguarde y no cierre esta pagina, la importacion puede durar varios minutos"
                        />
                      </div>
                    ) : null}

                    <Divider style={{ margin: '28px 0 20px' }} />

                    <div
                      style={{
                        background: '#fff7f4',
                        border: `1px solid ${ACCENT}33`,
                        borderRadius: 10,
                        padding: '16px 18px',
                      }}
                    >
                      <Space align="start" size={10}>
                        <BulbOutlined style={{ color: ACCENT, fontSize: 18, marginTop: 2 }} />
                        <div>
                          <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            Próximo paso
                          </Text>
                          <Paragraph style={{ margin: 0, color: 'rgba(0,0,0,0.65)', fontSize: 14 }}>
                            Al importar se genera el <Text strong>movimiento</Text> (pago contado) y la
                            cuenta de contraparte si no existía. En <Text strong>Pendientes</Text> solo
                            completás plan de cuentas y centro de costo. Los duplicados (pendiente o
                            movimiento igual) se omiten.
                          </Paragraph>
                        </div>
                      </Space>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: 'pend',
              label: (
                <Space size={6}>
                  <span>Pendientes</span>
                  <Tag
                    style={{
                      marginInlineEnd: 0,
                      borderRadius: 8,
                      ...(totalSinFiltro > 0
                        ? { background: ACCENT, color: '#fff', border: 'none' }
                        : {}),
                    }}
                  >
                    {totalSinFiltro}
                  </Tag>
                </Space>
              ),
              children: (
              <div style={{ padding: '20px 24px 24px' }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card
                  size="small"
                  title={<Text strong>Filtros</Text>}
                  style={{ borderRadius: 10, background: '#fafafa' }}
                  styles={{ body: { paddingBottom: 8 } }}
                >
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Tipo
                    </Text>
                    <Select
                      value={filterTipo}
                      onChange={setFilterTipo}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'all', label: 'Todos' },
                        { value: '1', label: 'Compras' },
                        { value: '2', label: 'Ventas' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={5}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Cuenta (CUIT)
                    </Text>
                    <Select
                      value={filterVinculoCuenta}
                      onChange={setFilterVinculoCuenta}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'all', label: 'Todas' },
                        { value: 'con', label: 'Con cuenta sugerida' },
                        { value: 'sin', label: 'Sin cuenta en sistema' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Comprobante (PV-número o parte)
                    </Text>
                    <Input
                      allowClear
                      placeholder="Ej: 2-1543 o 1543"
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                      value={filterComprobante}
                      onChange={(e) => setFilterComprobante(e.target.value)}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Fechas (desde — hasta)
                    </Text>
                    <RangePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      value={filterFechas}
                      onChange={(v) => setFilterFechas(v)}
                      placeholder={['Desde', 'Hasta']}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      disabled={!hayFiltros}
                      onClick={() => {
                        setFilterTipo('all');
                        setFilterComprobante('');
                        setFilterFechas(null);
                        setFilterVinculoCuenta('all');
                      }}
                    >
                      Limpiar
                    </Button>
                  </Col>
                </Row>
                </Card>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {hayFiltros ? (
                    <Text type="secondary">
                      Mostrando {pendientes.length} de {totalSinFiltro} registros
                    </Text>
                  ) : (
                    <span />
                  )}
                  <Button
                    danger
                    disabled={totalSinFiltro === 0}
                    onClick={eliminarTodosPendientes}
                  >
                    Eliminar todos los pendientes
                  </Button>
                </div>
                <Table
                  rowKey="aip_id"
                  loading={loadingList}
                  dataSource={pendientes}
                  columns={columns}
                  tableLayout="fixed"
                  style={{ width: '100%' }}
                  pagination={{
                    pageSize: 15,
                    showSizeChanger: true,
                    pageSizeOptions: ['15', '30', '50', '100'],
                  }}
                />
              </Space>
              </div>
            ),
          },
        ]}
        />

      <Modal
        title="Imputar movimiento"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setRowImputar(null);
        }}
        onOk={confirmarImputar}
        confirmLoading={imputando}
        width={560}
        destroyOnClose
      >
        {rowImputar && (
          <div style={{ marginBottom: 16 }}>
            <Text>
              {pModal.fecha} · {pModal.pto_vta}-{pModal.nro_comp} · Total{' '}
              {Number(pModal.mov_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </Text>
            {pModal.observaciones ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{pModal.observaciones}</Text>
              </div>
            ) : null}
            {!pModal.cue_sugerida_id && pModal.cuit_contraparte && rowImputar?.aip_movimiento == null ? (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message="Sin cuenta asociada a este CUIT"
                description={
                  <>
                    No existe una cuenta en esta empresa con CUIT {pModal.cuit_contraparte}. Debe
                    elegir manualmente la cuenta (contraparte) abajo o dar de alta el proveedor/cliente
                    en Cuentas.
                  </>
                }
              />
            ) : null}
            {rowImputar?.aip_movimiento != null ? (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message="Movimiento ya registrado"
                description="Solo falta imputar plan de cuentas y centro de costo. La contraparte y el pago contado ya están cargados."
              />
            ) : null}
          </div>
        )}
        <Form form={form} layout="vertical">
          {rowImputar?.aip_movimiento != null ? (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Contraparte (ya vinculada al movimiento)
              </Text>
              <Text>
                {pModal.cue_sugerida_nombre || '—'}{' '}
                {pModal.cuit_contraparte ? (
                  <Text type="secondary">· CUIT {pModal.cuit_contraparte}</Text>
                ) : null}
              </Text>
            </div>
          ) : (
            <Form.Item
              name="mov_cuenta"
              label="Cuenta (contraparte)"
              rules={[{ required: true, message: 'Obligatorio' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="CUIT / razón social"
                options={cuentas.map((c) => ({
                  value: c.cue_id,
                  label: `${c.cue_cuit} — ${c.cue_nombre}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="des_plancuenta"
            label="Plan de cuentas (imputación)"
            rules={[{ required: true, message: 'Obligatorio' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={planes.map((p) => ({
                value: p.pla_id,
                label: [p.pla_codigo, p.pla_nombre].filter(Boolean).join(' — ') || String(p.pla_id),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="des_centro"
            label="Centro de costo"
            rules={[{ required: true, message: 'Obligatorio' }]}
          >
            <Select
              options={centros.map((c) => ({
                value: c.cen_id,
                label: c.cen_nombre || String(c.cen_id),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="mov_tipocomprobante"
            label="Tipo de comprobante (si no se resolvió desde AFIP)"
            rules={[
              {
                validator: (_, v) => {
                  const p = parsePayload(rowImputar?.aip_payload);
                  if (!p.tic_id && !v) {
                    return Promise.reject(new Error('Seleccione el tipo'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              allowClear
              placeholder={pModal.tic_id ? 'Usar del CSV (opcional cambiar)' : 'Obligatorio si no hay match'}
              options={tiposComp.map((t) => ({
                value: t.tic_id,
                label: `${t.tic_codigo} — ${t.tic_nombre}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
    </div>
  );
};

export default ImportarMovimientoAdministrador;
